/**
 * Epic 9 — Doctrine Requirements Panel.
 *
 * Shows the requirement corpus fetched from /api/requirements for the current
 * incident context: which requirements are active, which are stand-ins, and
 * the governance status of each. Read-only — SME editing is a future step.
 *
 * Gated to reviewer-sme / admin roles (config.manage capability) so operational
 * crews don't see governance detail unless they need it.
 */

import { useEffect, useMemo, useState } from 'react';
import { BookMarked, AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, Clock, Info } from 'lucide-react';
import {
  requirementApplicabilityMatches,
  activeIncidentTypes,
  type DoctrineRequirement,
  type GovernanceStatus,
  type RequirementParameterKey,
  type Incident,
} from '@duty/shared';

// ── Labels ─────────────────────────────────────────────────────────────────────

const PARAM_LABELS: Record<RequirementParameterKey, string> = {
  minBaOperators: 'Min. BA operators',
  hydrantConcernDistanceM: 'Hydrant concern distance',
  sustainedFlowLpm: 'Sustained flow',
  bulkWaterMinL: 'Bulk water carrier',
  limitedOnboardL: 'Limited onboard water',
  evStorageExclusionDistanceM: 'EV battery storage exclusion',
  evBatterySuppressionMinL: 'EV battery suppression water',
};

const OPERATOR_LABELS: Record<string, string> = {
  min: '≥',
  max: '≤',
  eq: '=',
};

const STATUS_STYLES: Record<GovernanceStatus, { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'text-emerald-600 dark:text-emerald-400' },
  'sme-review': { label: 'SME review', className: 'text-amber-500' },
  extracted: { label: 'Extracted', className: 'text-blue-500' },
  superseded: { label: 'Superseded', className: 'text-muted line-through' },
};

// ── Component ──────────────────────────────────────────────────────────────────

export function RequirementsPanel({ incident }: { incident: Incident | null }) {
  const [requirements, setRequirements] = useState<DoctrineRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch('/api/requirements')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { requirements?: DoctrineRequirement[] } | null) => {
        if (active) {
          setRequirements(data?.requirements ?? []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const contextQuery = useMemo(() => ({
    state: 'NSW' as const,
    agencies: incident ? [incident.leadAgency, ...incident.supportingAgencies] : undefined,
    incidentTypes: incident ? activeIncidentTypes(incident) : undefined,
  }), [incident]);

  const applicable = useMemo(
    () => requirements.filter((r) => requirementApplicabilityMatches(r, contextQuery)),
    [requirements, contextQuery],
  );

  const standInCount = applicable.filter((r) => r.standIn && r.status === 'approved').length;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="space-y-2">
      {/* Header — collapsible */}
      <button
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <BookMarked size={12} />
          <span>
            {loading
              ? 'Loading requirements…'
              : `${applicable.length} requirement${applicable.length !== 1 ? 's' : ''} active`}
          </span>
          {standInCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-amber-500">
              <AlertTriangle size={10} />
              {standInCount} stand-in{standInCount > 1 ? 's' : ''}
            </span>
          )}
        </span>
        {open ? <ChevronDown size={12} className="text-muted" /> : <ChevronRight size={12} className="text-muted" />}
      </button>

      {open && (
        <div className="space-y-1.5">
          {applicable.length === 0 && !loading && (
            <p className="text-xs text-muted">No applicable requirements found for this context.</p>
          )}

          {applicable.map((req) => {
            const isExpanded = expanded.has(req.id);
            const statusStyle = STATUS_STYLES[req.status];
            const op = OPERATOR_LABELS[req.parameter.operator] ?? req.parameter.operator;
            const paramLabel = PARAM_LABELS[req.parameter.key] ?? req.parameter.key;

            return (
              <div
                key={req.id}
                className="rounded-md border border-border bg-surface-2/40 text-[11px]"
              >
                <button
                  className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
                  onClick={() => toggle(req.id)}
                  aria-expanded={isExpanded}
                >
                  {/* Status icon */}
                  <span className="mt-0.5 shrink-0">
                    {req.status === 'approved' ? (
                      <CheckCircle2 size={11} className="text-emerald-500" />
                    ) : (
                      <Clock size={11} className="text-amber-500" />
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-text">{paramLabel}</span>
                    <span className="ml-1.5 text-accent">
                      {op} {req.parameter.value}{req.parameter.unit ? ` ${req.parameter.unit}` : ''}
                    </span>
                  </span>

                  <span className="flex shrink-0 items-center gap-1.5">
                    {req.standIn && (
                      <span className="rounded bg-amber-500/10 px-1 text-[10px] text-amber-500">
                        stand-in
                      </span>
                    )}
                    <span className={`text-[10px] ${statusStyle.className}`}>
                      {statusStyle.label}
                    </span>
                    {isExpanded
                      ? <ChevronDown size={10} className="text-muted" />
                      : <ChevronRight size={10} className="text-muted" />
                    }
                  </span>
                </button>

                {isExpanded && (
                  <div className="space-y-1.5 border-t border-border px-2.5 pb-2.5 pt-2 text-[10px] text-muted">
                    <p className="flex items-start gap-1">
                      <Info size={9} className="mt-0.5 shrink-0" />
                      <span>{req.sourceTitle}</span>
                    </p>
                    {req.notes && (
                      <p className="text-amber-500/80">{req.notes}</p>
                    )}
                    {req.outcomes.length > 0 && (
                      <ul className="space-y-0.5 pl-2">
                        {req.outcomes.map((o, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="shrink-0 font-medium capitalize">{o.type}:</span>
                            <span>{o.description}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-muted/60">
                      Extracted {req.extractedAt}
                      {req.approvedAt ? ` · approved ${req.approvedAt}` : ''}
                      {req.standIn ? ' · Replace with authoritative source before agency adoption.' : ''}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {requirements.length > applicable.length && (
            <p className="text-[10px] text-muted">
              {requirements.length - applicable.length} requirement{requirements.length - applicable.length !== 1 ? 's' : ''} exist but don't apply to this incident type or context.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
